<template>
  <v-container fluid class="rv-page">
    <!-- Warning Alert -->
    <AAlertBanner :messages="alertError" title="Warning" />

    <!-- Danger Alert -->
    <AAlertBanner variant="danger" :messages="alertDanger" title="Alert" :icon-size="22" />

    <!-- Approve Confirmation Dialog -->
    <TheModalConfirmation ref="approveConfirmPopup"></TheModalConfirmation>

    <!-- Reject Confirmation Dialog -->
    <TheModalConfirmation ref="rejectConfirmPopup"></TheModalConfirmation>

    <!-- Header -->
    <div class="rv-header">
      <div class="rv-header__top">

        <div v-if="!loadingDialog" class="rv-header__info">
          <h1 class="rv-header__title">{{ model.add.name }}</h1>
        </div>

        <div class="rv-header__actions" v-if="isTaxpodApproval1AdminRoles && !btnDisabled && !editMode">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--red"
                :disabled="apiSubmit.isLoading"
                @click="toggleRejectDialog()"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18">mdi-file-remove</v-icon>
              </button>
            </template>
            <span>Reject Refund Email</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--green"
                :disabled="apiSubmit.isLoading"
                @click="toggleApproveDialog()"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18">mdi-check-circle-outline</v-icon>
              </button>
            </template>
            <span>Submit</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- Split Pane: Document Viewer (left) + Panel (right) -->
    <div class="split-pane">
      <!-- LEFT: Document Viewer -->
      <div class="split-pane__viewer">
        <div class="viewer-card">
          <div class="viewer-card__tabs">
            <button
              v-for="(attn, index) in refundEmailAttachments"
              :key="index"
              class="viewer-tab"
              :class="{ 'viewer-tab--active': activeTab === index }"
              @click="previewOrDownloadAttachment(index, 'preview'); activeTab = index"
            >
              <v-icon size="14" class="mr-1">mdi-file-eye-outline</v-icon>
              {{ attn }}
            </button>
            <button
              v-for="(otherAttn, index) in otherRefundEmailAttachments"
              :key="'other-' + index"
              class="viewer-tab"
              :class="{ 'viewer-tab--active': activeTab === index }"
              @click.prevent="previewOrDownloadAttachment(index, 'preview'); activeTab = index"
            >
              <v-icon size="14" class="mr-1">mdi-file-eye-outline</v-icon>
              {{ otherAttn }}
            </button>
          </div>
          <div class="viewer-card__body">
            <iframe
              v-if="imageUrl"
              :src="imageUrl"
              width="100%"
              height="100%"
              class="viewer-card__iframe"
            ></iframe>
            <div v-else class="viewer-card__empty">
              <v-icon size="56" color="#cbd5e1">mdi-file-search-outline</v-icon>
              <span class="viewer-card__empty-text">Select a document to preview</span>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Info Panel -->
      <div class="split-pane__panel">
        <!-- Refund Details -->
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-cash-refund</v-icon>
            <span class="section-header__title">Refund Details</span>
          </div>
          <div class="section-body">
            <div class="detail-row">
              <span class="detail-label">taxPOD Price</span>
              <span class="detail-val">{{ formattedPrice }}</span>
            </div>
            <div class="panel-form-group">
              <label class="panel-form-label">Refund Remarks</label>
              <textarea
                v-model="formData.refundRemarks"
                class="panel-textarea"
                rows="3"
                placeholder="Enter refund remarks..."
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Refund Checklist -->
        <div class="section-card section-card--grow">
          <div class="section-header section-header--green">
            <v-icon color="white" small>mdi-clipboard-check-outline</v-icon>
            <span class="section-header__title">Refund Checklist</span>
            <v-spacer></v-spacer>
            <span class="checklist-counter">{{ refundCheckedlist.length }}/{{ refundChecklistItems.length }}</span>
          </div>
          <!-- Sticky progress strip -->
          <div class="checklist-progress-strip">
            <div class="checklist-progress">
              <div
                class="checklist-progress__bar"
                :style="{ width: (refundCheckedlist.length / refundChecklistItems.length * 100) + '%' }"
                :class="{ 'checklist-progress__bar--complete': refundCheckedlist.length === refundChecklistItems.length }"
              ></div>
            </div>
            <span class="checklist-progress-label">
              <template v-if="refundCheckedlist.length === refundChecklistItems.length">
                <v-icon size="14" color="#10b981" class="mr-1">mdi-check-circle</v-icon>
                All verified
              </template>
              <template v-else>
                {{ refundChecklistItems.length - refundCheckedlist.length }} remaining
              </template>
            </span>
          </div>
          <div class="section-body section-body--compact">
            <div v-if="checklistUncheckErr" class="checklist-error">
              <v-icon small color="#dc2626" class="mr-2">mdi-alert-circle-outline</v-icon>
              Please check all the refund details!
            </div>

            <v-list flat class="checklist-list">
              <v-list-item-group v-model="refundCheckedlist" multiple>
                <template v-for="(rcItem, index) in refundChecklistItems">
                  <v-list-item
                    :key="index"
                    class="checklist-item"
                    :class="{ 'checklist-item--checked': refundCheckedlist.includes(index) }"
                  >
                    <template v-slot:default="{ active }">
                      <v-list-item-action class="checklist-item__action">
                        <div class="custom-check" :class="{ 'custom-check--active': active }">
                          <v-icon v-if="active" size="14" color="#fff">mdi-check</v-icon>
                        </div>
                      </v-list-item-action>
                      <v-list-item-content class="checklist-item__content">
                        <v-list-item-title class="checklist-item__title">{{ rcItem.text }}</v-list-item-title>
                        <v-list-item-subtitle class="checklist-item__subtitle">
                          <span v-html="rcItem.value"></span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <div class="checklist-item__status">
                        <v-icon v-if="active" size="16" color="#10b981">mdi-check-circle</v-icon>
                        <v-icon v-else size="16" color="#cbd5e1">mdi-circle-outline</v-icon>
                      </div>
                    </template>
                  </v-list-item>
                </template>
              </v-list-item-group>
            </v-list>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden Datepicker -->
    <v-col cols="12" sm="6" md="6" v-show="false">
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="date"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            dense
            filled
            v-model="formData.refundEmailApproval1Date"
            label="Refund Email Approval 1 Date (YYYY-MM-DD)"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
            :disabled="true"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="formData.refundEmailApproval1Date"
          no-title
          scrollable
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="menu = false"> Cancel </v-btn>
          <v-btn text color="primary" @click="$refs.menu.save(date)">
            OK
          </v-btn>
        </v-date-picker>
      </v-menu>
    </v-col>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc_event/objects/globalData";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";

export default {
  mixins: [hrdcFunction],
  components: {
    TheModalConfirmation,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    formattedPrice() {
      return `RM ${parseFloat(this.formData.taxpodPrice).toLocaleString()}`;
    },
  },
  props: ["params"],
  data: () => ({
    apiGetApplicationSingleDetail: new Api(),
    // apiGetRefundAttachment: new Api(),
    apiScanPdfText: new Api(),
    apiGetFileAttachment: new Api(),
    apiDownloadOrPreviewAttachment: new Api(),
    apiSubmit: new Api(),
    apiReject: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    alertDanger: [],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu: false,
    formData: {
      applicationNo: null,
      taxpodPrice: null,
      refundEmailApproval1Date: new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substr(0, 10),
      remarks: null,
      manualInvoiceToHrdc: null,
      manualInvoiceToHrdc2: null,
      // manualInvoice: {
      //   name: "HRDC Invoice",
      // },
      // ssRefundBankAccDetails: {
      //   name: "Screenshot of Refund Bank Account Details.png",
      // },
      // autoGeneratedTaxpodInvoice: [],
      autoGeneratedClientInvoiceNo: null,
      // paymentAdviceFromHrdc: null,
      // paymentAdviceFromClient: null,
      paymentAdviceType: "Stripe",
      // creditNote: [],
      creditNoteNo: null,
      oldCompanyName: null,
      oldSsmNo: null,
      bankUuid: null,
      oldBankAccount: null,
      paymentReceivedDate: null,
      // refundOtherAttachments: [],
      refundRemarks: null,
      oldBankAccountHolderName: null,
    },
    errorField: {
      applicationNo: null,
      refundEmailApproval1Date: null,
      remarks: null,
      manualInvoice: null,
      manualInvoiceToHrdc: null,
      refundOtherAttachments: null,
    },
    refundEmailAttachments: {
      autoGeneratedInvoice: "Client/taxPOD Invoice",
      creditNoteGenerated: "Credit Note",
      manualInvoiceToHrdc: "HRDC Invoice",
      paymentAdviceFromClient: "Payment Advice From Client",
      paymentAdviceFromHrdc: "Payment Advice From HRDC",
      ssRefundBankAccDetails: "Screenshot Refund Bank Account Details",
    },
    otherRefundEmailAttachments: {},
    loadingDialog: false,
    refundEmailApproval1SubmitDialog: false,
    refundEmailRejectDialog: false,
    editMode: false,
    btnDisabled: false,
    paymentAdviceItems: null,
    imageUrl: null,
    panel: [0],
    activeTab: 'autoGeneratedInvoice',
    refundCheckedlist: [],
    refundChecklistItems: [
      {
        text: "Client Invoice No.",
        slug: "autoGeneratedClientInvoiceNo",
        value: null,
        read: true,
      },
      {
        text: "HRDC Invoice No.",
        slug: "manualInvoiceToHrdc",
        value: null,
        read: true,
      },
      {
        text: "Payment Advice from Client",
        slug: "paymentAdviceType",
        value: null,
        read: true,
      },
      {
        text: "Payment Advice from HRDC",
        slug: "manualInvoiceToHrdc",
        value: null,
        read: true,
      },
      {
        text: "Credit Note",
        slug: "creditNoteNo",
        value: null,
        read: true,
      },
      {
        text: "Screenshot of Bank Account Details",
        slug: "ssOfBankAccDetails",
        value: null,
        read: false,
      },
      {
        text: "Refund Bank Account Details",
        slug: "refundBankAccDetails",
        value: null,
        read: false,
      },
    ],
    checklistUncheckErr: false,
  }),
  watch: {
    "formData.creditNoteNo": function (newVal) {
      let creditNoteObj = this.refundChecklistItems.find(
        (item) => item.slug === "creditNoteNo"
      );

      if (newVal.length > 0) {
        if (creditNoteObj) {
          if (typeof this.formData.creditNoteNo === "string") {
            creditNoteObj.value = this.formData.creditNoteNo;
          } else {
            creditNoteObj.value = this.formData.creditNoteNo.join(",");
          }
        }
      } else {
        creditNoteObj.value = null;
      }
    },
    "formData.paymentAdviceType": function (newVal) {
      let paymentAdviceTypeObj = this.refundChecklistItems.find(
        (item) => item.slug === "paymentAdviceType"
      );

      if (newVal) {
        paymentAdviceTypeObj.value = newVal;
      }
    },
  },
  async created() {
    this.loadingDialog = !this.loadingDialog;
    this.formData.applicationNo = this.$route.query._ausk;
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_approval_1/create-refund-email-approval-1`,
        method: "post",
      },
      apiGetApplicationSingleDetail: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-single-application`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiDownloadOrPreviewAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/azure-download-or-preview-application-attachment`,
      },
      apiGetFileAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-attachment`,
        method: "post",
      },
      // apiGetRefundAttachment: {
      //   url: `${
      //     this.$service[this.service.key]
      //   }/v1/en/console/application/get-attachment-array`,
      //   method: "post",
      // },
      apiScanPdfText: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_submission/scan-pdf`,
        method: "post",
      },
      apiReject: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/reject-refund-email`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    const currentStageId =
      this.hrdcData.stageIndex[this.$store.state[this.service.key].data.stageId];
    this.rolesAssignation(this.auth.roles);
    if (!this.isTaxpodApproval1AdminRoles) {
      this.alertError.push("You are not allowed to perform this action!");
      this.btnDisabled = true;
    }

    this.checkFormAlreadyFilledGte(
      currentStageId,
      this.hrdcData.stageSlug.refundEmailApproval1
    );

    this.checkFormNotYetFilledLesser(
      currentStageId,
      this.hrdcData.stageSlug.refundEmailSubmission
    );

    // Assign payment advice options.
    this.paymentAdviceItems = this.paymentAdviceOpts;

    this.apiGetApplicationSingleDetail.setCallbackCompleted((response) => {
      try {
        const { data, company } = response;
        const attachments = {};
        let otherRefundAttnCount = 0;
        const stagePriorities = [
          this.hrdcData.stagePriority.creditNote,
          this.hrdcData.stagePriority.creditNoteGenerated,
          this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice,
          this.hrdcData.stagePriority.paymentAdviceFromHrdc,
          this.hrdcData.stagePriority.paymentAdviceFromClient,
          this.hrdcData.stagePriority.refundEmailOtherAttachments,
          this.hrdcData.stagePriority.manualInvoiceToHrdc,
        ];

        if (this.editMode) {
          this.formData.refundEmailApproval1Date = moment(
            data.ApplicationLog.find(
              (obj) =>
                obj.stageId == this.hrdcData.stagePriority.refundEmailApproval1
            ).startDate
          ).format("YYYY-MM-DD");

          for (const stageId of stagePriorities) {
            let filesArr = [];
            const attachment = data.ApplicationAttachment.filter(
              (item) => item.stageId == stageId
            );

            if (attachment.length > 0) {
              for (const atch of attachment) {
                // Cater for other refund attachments.
                if (
                  stageId ==
                  this.hrdcData.stagePriority.refundEmailOtherAttachments
                ) {
                  if (!attachments[stageId]) {
                    attachments[stageId] = {};
                  }

                  attachments[stageId][otherRefundAttnCount] = {
                    fileUrl: atch.fileUrl,
                    fileName: atch.oriFileName,
                  };
                  otherRefundAttnCount++;
                } else {
                  filesArr.push(atch.fileUrl);

                  attachments[stageId] = {
                    fileName: attachment.oriFileName,
                  };
                }
              }

              attachments[stageId].fileUrl = filesArr;
            }
          }
        } else {
          // Attached authorization/any other attachment if got any.
          if (data.ApplicationAttachment.length > 0) {
            for (const attn of data.ApplicationAttachment) {
              if (attn.oriFileName) {
                this.otherRefundEmailAttachments[attn.oriFileName] =
                  attn.oriFileName;
              }
            }
          }

          for (const stageId of stagePriorities) {
            let filesArr = [];
            let checkMultipleAtch = [];
            const attachment = data.ApplicationAttachment.filter(
              (item) => item.stageId == stageId
            );

            if (attachment.length > 0) {
              for (const atch of attachment) {
                checkMultipleAtch.push(atch.stageId);
                // Cater for other refund attachments.
                if (
                  stageId ==
                  this.hrdcData.stagePriority.refundEmailOtherAttachments
                ) {
                  if (!attachments[stageId]) {
                    attachments[stageId] = {};
                  }

                  attachments[stageId][otherRefundAttnCount] = {
                    fileUrl: atch.fileUrl,
                    fileName: atch.oriFileName,
                  };
                  otherRefundAttnCount++;
                } else {
                  filesArr.push(atch.fileUrl);

                  if (checkMultipleAtch.length > 1) {
                    // Get attachment display name based on stageId.
                    switch (atch.stageId) {
                      case this.hrdcData.stagePriority.creditNoteGenerated:
                        this.refundEmailAttachments[
                          `creditNote[::]${checkMultipleAtch.length - 1}`
                        ] = `Credit Note - ${checkMultipleAtch.length}`;
                        break;

                      case this.hrdcData.stagePriority
                        .autoGeneratedTaxpodInvoice:
                        this.refundEmailAttachments[
                          `autoGeneratedInvoice[::]${
                            checkMultipleAtch.length - 1
                          }`
                        ] = `Client/taxPOD Invoice - ${checkMultipleAtch.length}`;
                        break;

                      case this.hrdcData.stagePriority.manualInvoiceToHrdc:
                        this.refundEmailAttachments[
                          `manualInvoiceToHrdc[::]${
                            checkMultipleAtch.length - 1
                          }`
                        ] = `HRDC Invoice - ${checkMultipleAtch.length}`;
                        break;
                    }
                  }

                  attachments[stageId] = {
                    fileName: atch.oriFileName,
                  };
                }
              }

              attachments[stageId].fileUrl = filesArr;
            }
          }

          let paymentReceivedDate = data.ApplicationLog.find(
            (item) =>
              item.stageId ==
              this.hrdcData.stagePriority.paymentReceivedFromHrdc
          );

          if (paymentReceivedDate) {
            this.formData.paymentReceivedDate = moment(
              paymentReceivedDate.startDate,
              "YYYY-MM-DD HH:mm:ssZ"
            ).format("D MMMM YYYY");
          }
        }

        // Assign data section.
        this.formData.taxpodPrice = data.totalPrice;
        this.formData.oldBankAccountHolderName =
          data.oldBankAccountHolderName.toUpperCase();

        // Check if pricing is unsual, display warning.
        if (!this.staticTaxpodPricing.includes(Number(data.totalPrice))) {
          this.alertDanger.push("Detected Unusual Price, Please Take Note");
        }

        this.formData.manualInvoiceToHrdc = data.manualInvoiceToHrdc;
        this.formData.manualInvoiceToHrdc2 = data.manualInvoiceToHrdc2;
        this.formData.autoGeneratedClientInvoiceNo =
          data.autoGeneratedClientInvoiceNo;
        this.formData.creditNoteNo = data.creditNoteNo;
        this.formData.paymentAdviceType = data.paymentAdviceFromClientType;

        // Assign company data.
        this.formData.oldCompanyName = this.capitalizeFirstLetter(
          company.oldCompanyName
        );
        this.formData.oldSsmNo = company.oldSsmNo;
        this.formData.bankUuid = data.bankUuid;
        this.formData.oldBankAccount = data.oldBankAccount;
        this.formData.refundRemarks = data.refundRemarks;

        // Get refund email attachment.
        // this.apiGetRefundAttachment.setParams({
        //   applicationUuid: this.$route.query._ausk,
        //   attachments,
        // });

        this.loadingDialog = !this.loadingDialog;

        // this.apiGetRefundAttachment.setCallbackCompleted((response) => {
        //   try {
        //     const { status, attn } = response;
        //     if (status) {
        //       for (const item of attn) {
        //         // Create a Blob object from the data
        //         const blob = new Blob([new Uint8Array(item.dataArray)]);

        //         let fileName = null;
        //         let file = null;

        //         // Cater for other refund email attachment.
        //         if (item.other == true) {
        //           switch (item.type) {
        //             case "refundEmailOtherAttachments":
        //               file = new File([blob], item.fileName, {
        //                 type: `application/${item.extension}`,
        //               });

        //               this.formData.refundOtherAttachments.push(file);
        //               break;
        //           }
        //           continue;
        //         }

        //         switch (Number(item.type)) {
        //           case this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice:
        //             fileName = "Client/taxPOD Invoice";
        //             // Create the File object
        //             file = new File([blob], fileName, {
        //               type: `application/${item.extension}`,
        //             });

        //             this.formData.autoGeneratedTaxpodInvoice.push(file);
        //             break;

        //           case this.hrdcData.stagePriority.creditNote:
        //           case this.hrdcData.stagePriority.creditNoteGenerated:
        //             fileName = "Credit Note";
        //             // Create the File object
        //             file = new File([blob], fileName, {
        //               type: `application/${item.extension}`,
        //             });

        //             this.formData.creditNote.push(file);
        //             break;

        //           case this.hrdcData.stagePriority.paymentAdviceFromHrdc:
        //             fileName = "Payment Advice From HRDC";
        //             // Create the File object
        //             file = new File([blob], fileName, {
        //               type: `application/${item.extension}`,
        //             });

        //             this.formData.paymentAdviceFromHrdc = file;
        //             break;

        //           case this.hrdcData.stagePriority.paymentAdviceFromClient:
        //             fileName = "Payment Advice From Client";
        //             // Create the File object
        //             file = new File([blob], fileName, {
        //               type: `application/${item.extension}`,
        //             });

        //             this.formData.paymentAdviceFromClient = file;
        //             break;
        //         }
        //       }
        //     }
        //     this.loadingDialog = !this.loadingDialog;
        //   } catch (err) {
        //     console.log(err);
        //   }
        // });

        // this.apiGetRefundAttachment.fetch();

        // Assign checklist items.
        this.assignRefundChecklistItems();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetApplicationSingleDetail.fetch();

    this.previewOrDownloadAttachment("autoGeneratedInvoice", "preview");
  },
  methods: {
    toggleApproveDialog() {
      this.$refs.approveConfirmPopup.open({
        title: this.model.name.singular,
        message: "Please confirm that you wish to Approve Refund Email",
        options: {
          btnDisagree: "Cancel",
          btnAgree: "Approve",
        },
        agree: () => {
          this.modalRefundEmailApproval1Submit();
        },
        disagree: () => {},
      });
    },
    toggleRejectDialog() {
      this.$refs.rejectConfirmPopup.open({
        title: "Reject Refund Email",
        message: "Please confirm that you wish to Reject this Refund Email",
        options: {
          btnDisagree: "Cancel",
          btnAgree: "Reject",
        },
        agree: () => {
          this.modalRefundEmailReject();
        },
        disagree: () => {},
      });
    },
    getAttachmentColorClass(index) {
      if (index.includes("autoGeneratedInvoice")) return "attn-pill--blue";
      if (index.includes("creditNote")) return "attn-pill--amber";
      if (index.includes("paymentAdvice")) return "attn-pill--green";
      if (index.includes("ssRefundBankAccDetails")) return "attn-pill--purple";
      if (index.includes("manualInvoice")) return "attn-pill--blue";
      return "attn-pill--gray";
    },
    clearErrorMsg(field = null) {
      if (!field) {
        for (let error in this.errorField) {
          this.errorField[error] = null;
        }
      } else {
        this.errorField[field] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    async previewOrDownloadAttachment(fileUrl, action) {
      this.loadingDialog = !this.loadingDialog;

      // Get the stageId for each file.
      const stageId = this.getStageIdFromFile(fileUrl);

      // Check if got specific delimeter to handle for multiple attachments.
      let multipleAttachmentIndex = 0;
      if (fileUrl.includes("[::]")) {
        multipleAttachmentIndex = fileUrl.split("[::]")[1];
      }

      this.apiGetFileAttachment.setParams({
        applicationUuid: this.formData.applicationNo,
        stageId,
      });

      // Get the file blob.
      this.apiGetFileAttachment.setCallbackCompleted(async (response) => {
        try {
          if (response.status) {
            const data = response.data;
            const applicationAttachment = data.ApplicationAttachment;

            if (applicationAttachment.length > 0) {
              const oriFileBlob =
                applicationAttachment[multipleAttachmentIndex].fileUrl;
              const attachment = oriFileBlob.split(".");
              const attachmentPathWithoutExt = attachment[0];
              const attachmentExt = attachment[1].toLowerCase();
              const fileBlob = `${attachmentPathWithoutExt}.${attachmentExt}`;

              let responseType = "blob";
              let isImage = false;
              let blobType = `application/${attachmentExt}`;
              let streamUrl = this.apiDownloadOrPreviewAttachment.url;
              if (this.isImage(fileBlob)) {
                isImage = true;
                blobType = `image/${attachmentExt}`;
              }

              await this.$axios
                .get(streamUrl, {
                  responseType,
                  headers: {
                    "Content-Type": "application/json",
                  },
                  params: {
                    filePath: fileBlob,
                    isImage,
                  },
                })
                .then((response) => {
                  if (action == "preview") {
                    let reader = new FileReader();
                    reader.onload = (event) => {
                      // Set base64 string as image source
                      let originalDataUrl = event.target.result;
                      let modifiedDataUrl = originalDataUrl.replace(
                        /^data:application\/octet-stream/,
                        `data:${blobType}`
                      );
                      this.imageUrl = `${modifiedDataUrl}`;
                    };
                    reader.readAsDataURL(response.data);
                  }
                })
                .catch((error) => {
                  if ((error.response.status == 404) | true) {
                    this.$store.dispatch("showMessage", {
                      message:
                        "Failed to Preview/Download attachment, kindly contact administrator",
                      messageType: "error",
                      timeout: 2000,
                    });
                  }
                });
            } else {
              this.$store.dispatch("showMessage", {
                message: "Attachment not found!",
                messageType: "error",
                timeout: 2000,
              });
              this.loadingDialog = !this.loadingDialog;
            }
          }
          this.loadingDialog = !this.loadingDialog;
        } catch (err) {
          this.$store.dispatch("showMessage", {
            message:
              "Failed to Preview/Download attachment, kindly contact administrator",
            messageType: "error",
            timeout: 2000,
          });
          this.loadingDialog = !this.loadingDialog;
        }
      });
      this.apiGetFileAttachment.fetch();
    },
    isImage(url) {
      return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
    },
    checkAttachment(e, field) {
      if (e) {
        this.clearErrorMsg(field);
      }
    },
    getStageIdFromFile(file) {
      let stage;

      if (file.includes("creditNote")) {
        return this.hrdcData.stagePriority.creditNote;
      }

      if (file.includes("autoGeneratedInvoice")) {
        return this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice;
      }

      if (file.includes("manualInvoiceToHrdc")) {
        return this.hrdcData.stagePriority.manualInvoiceToHrdc;
      }

      switch (file) {
        case "autoGeneratedInvoice":
          stage = this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice;
          break;
        case "creditNote":
          stage = this.hrdcData.stagePriority.creditNote;
          break;
        case "creditNoteGenerated":
          stage = this.hrdcData.stagePriority.creditNoteGenerated;
          break;
        case "ssRefundBankAccDetails":
          stage = this.hrdcData.stagePriority.applicationSubmitted;
          break;
        case "manualInvoiceToHrdc":
          stage = this.hrdcData.stagePriority.manualInvoiceToHrdc;
          break;
        case "paymentAdviceFromClient":
          stage = this.hrdcData.stagePriority.paymentAdviceFromClient;
          break;
        case "paymentAdviceFromHrdc":
          stage = this.hrdcData.stagePriority.paymentAdviceFromHrdc;
          break;
        default:
          stage = file;
          break;
      }

      return stage;
    },
    capitalizeFirstLetter(str) {
      return str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    modalRefundEmailApproval1Submit() {
      this.refundEmailApproval1SubmitDialog =
        !this.refundEmailApproval1SubmitDialog;
      this.submit();
    },
    redirectBack() {
      let route = this.eventRoute("Application");
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state[this.service.key].data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    addDropFile(e, key, fileType, matchExtType) {
      let file = e.dataTransfer.files[0];

      if (matchExtType) {
        if (!file.type.match(matchExtType)) {
          switch (matchExtType) {
            case "pdf.*":
              this.errorField[key] = "Please select pdf file format";
              break;
            case "image/*":
              this.errorField[key] = "Please select only image";
              break;
          }
          return;
        }
      }

      this.formData[key] = file;
      if (fileType) {
        this.scanPdf(e, key, fileType);
      }

      if (e) {
        this.clearErrorMsg(key);
      }
    },
    addDropMultipleFile(e, key, fileType, matchExtType) {
      let files = e.dataTransfer.files;

      for (const file of files) {
        if (matchExtType) {
          if (!file.type.match(matchExtType)) {
            switch (matchExtType) {
              case "pdf.*":
                this.errorField[key] = "Please select pdf file format";
                break;
              case "image/*":
                this.errorField[key] = "Please select only image";
                break;
            }
            return;
          }
        }

        this.formData[key].push(file);

        if (fileType) {
          this.scanPdf(e, key, fileType);
        }
      }

      if (e) {
        this.clearErrorMsg(key);
      }
    },
    scanPdf($event, fileType, displayFileInvoiceNo) {
      this.clearErrorMsg(fileType);
      this.clearErrorMsg(displayFileInvoiceNo);
      if ($event) {
        this.loadingDialog = !this.loadingDialog;
        const fd = new FormData();

        let counter = 0;
        for (const file of this.formData[fileType]) {
          fd.append(`${fileType}_${counter}`, file);
          counter++;
        }

        fd.append("fileType", fileType);
        fd.append("counter", counter);

        this.apiScanPdfText.setParams(fd);
        this.apiScanPdfText.setCallbackCompleted((response) => {
          try {
            const { data } = response;
            if (response.status) {
              this.formData[displayFileInvoiceNo] = data;
            } else {
              this.formData[displayFileInvoiceNo] = null;
              this.$store.dispatch("showMessage", {
                message: data,
                messageType: "error",
                timeout: 2000,
              });
            }
            this.loadingDialog = !this.loadingDialog;
          } catch (err) {
            console.log(err);
          }
        });
        this.apiScanPdfText.fetch();
      } else {
        // Empty the invoice no. if remove attachment.
        this.formData[displayFileInvoiceNo] = null;
      }
    },
    getFileExt(fileType) {
      if (fileType) {
        // Get the file extension type.
        const fileSplit = fileType.split("/");
        return fileSplit[1];
      }

      return fileType;
    },
    assignRefundChecklistItems() {
      for (const refundItem of this.refundChecklistItems) {
        if (refundItem.read) {
          refundItem.value = this.formData[refundItem.slug];
        }
      }

      // Assign refund bank account details.
      let refundBankAccObj = this.refundChecklistItems.find((item) => {
        return item.slug === "refundBankAccDetails";
      });

      if (refundBankAccObj) {
        refundBankAccObj.value = `Company Name: ${this.formData.oldBankAccountHolderName}<br/>Company Registration No.: ${this.formData.oldSsmNo}<br/>Bank: ${this.formData.bankUuid}<br/>Bank Account Number: ${this.formData.oldBankAccount}`;
      }

      // Check is there is any 30% HRDC Inv No.
      if (this.formData.manualInvoiceToHrdc2) {
        const hrdcObj = this.refundChecklistItems.filter(
          (item) => item.slug == "manualInvoiceToHrdc"
        );

        if (hrdcObj.length > 0) {
          for (const obj of hrdcObj) {
            obj.value += `, ${this.formData.manualInvoiceToHrdc2}`;
          }
        }
      }
    },
    async submit() {
      // const changeFileExtensions = ["paymentAdviceFromClient"];

      // for (const file of changeFileExtensions) {
      //   if (this.formData[file]) {
      //     let oriFileExt = await this.getOriginalAttachmentFileExtension(
      //       this.formData[file]
      //     );

      //     this.formData[file] = new File(
      //       [this.formData[file]],
      //       this.formData[file].name,
      //       {
      //         type: oriFileExt,
      //       }
      //     );
      //   }
      // }

      // Ensure all refund checklist is checked.
      if (this.refundCheckedlist.length !== this.refundChecklistItems.length) {
        this.checklistUncheckErr = true;
        return false;
      }

      this.loadingDialog = !this.loadingDialog;

      const fd = new FormData();
      fd.append("applicationUuid", this.$route.query._ausk);
      fd.append("totalPrice", this.formData.taxpodPrice);
      fd.append(
        "refundEmailApproval1Date",
        this.formData.refundEmailApproval1Date
      );

      // Loop taxPOD invoice.
      // let taxpodInvoiceCounter = 0;
      // for (const taxpodInvoice of this.formData.autoGeneratedTaxpodInvoice) {
      //   fd.append(
      //     `autoGeneratedTaxpodInvoice_${taxpodInvoiceCounter}`,
      //     taxpodInvoice
      //   );
      //   taxpodInvoiceCounter++;
      // }
      // fd.append("taxpodInvoiceCounter", taxpodInvoiceCounter);

      fd.append(
        "autoGeneratedClientInvoiceNo",
        this.formData.autoGeneratedClientInvoiceNo
      );

      // let cnCounter = 0;
      // for (const cn of this.formData.creditNote) {
      //   fd.append(`creditNote_${cnCounter}`, cn);
      //   cnCounter++;
      // }

      // fd.append("cnCounter", cnCounter);
      fd.append("creditNoteNo", this.formData.creditNoteNo);
      // fd.append(
      //   "paymentAdviceFromClient",
      //   this.formData.paymentAdviceFromClient
      // );
      // fd.append("paymentAdviceFromHrdc", this.formData.paymentAdviceFromHrdc);
      // fd.append("paymentAdviceType", this.formData.paymentAdviceType);
      fd.append("referById", this.auth.uuid);
      fd.append("editMode", this.editMode);
      fd.append("refundRemarks", this.formData.refundRemarks);

      // Assign multiple attachments to Form Data.
      // for (let i = 0; i < this.formData.refundOtherAttachments.length; i++) {
      //   fd.append(
      //     `refundOtherAttachments_${i}`,
      //     this.formData.refundOtherAttachments[i]
      //   );
      // }

      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.loadingDialog = !this.loadingDialog;
          if (!response.status) {
            this.showErrorMessage(response);
            if (response.message) {
              this.$store.dispatch("showMessage", {
                message: response.message,
                messageType: "error",
                timeout: 2000,
              });
            }
          }

          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: this.eventRoute("Application"),
              query: {
                viewId: this.$store.state[this.service.key].data.viewId,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });

      this.apiSubmit.setParams(fd);
      this.apiSubmit.fetch();
    },
    modalRefundEmailReject() {
      this.loadingDialog = !this.loadingDialog;

      this.apiReject.setParams({
        applicationUuid: this.$route.query._ausk,
        userUuid: this.auth.uuid,
        rejectToStage: this.hrdcData.stagePriority.creditNoteGenerated,
        rejectStageId: this.hrdcData.stagePriority.refundEmailApproval1Rejected,
        remarks: this.formData.refundRemarks,
      });

      this.apiReject.setCallbackCompleted((response) => {
        try {
          this.loadingDialog = !this.loadingDialog;
          if (response.status) {
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });

            this.$router.push({
              name: this.eventRoute("Application"),
              query: {
                viewId: this.$store.state[this.service.key].data.viewId,
              },
            });
          }
        } catch (err) {
          this.loadingDialog = !this.loadingDialog;
          console.log(err);
        }
      });

      this.apiReject.fetch();

      this.refundEmailRejectDialog = false;
    },
  },
};
</script>

<style>
/* Unscoped styles for Vuetify deep overrides */
.rv-page .v-list {
  background: transparent !important;
}

.rv-page .checklist-list .v-list-item {
  padding: 10px 12px !important;
  min-height: auto !important;
}

.rv-page .checklist-list .v-list-item__action {
  margin: 0 12px 0 0 !important;
}

.rv-page .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-page {
  background-color: var(--app-page);
}

.dark-mode .rv-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn--red {
  background: var(--app-danger-bg);
}

.dark-mode .hero-action-btn--red:hover {
  background: var(--app-danger-bg);
}

.dark-mode .hero-action-btn--green {
  background: var(--app-success-bg);
}

.dark-mode .hero-action-btn--green:hover {
  background: var(--app-success-bg);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .detail-val {
  border: 1px solid var(--app-border);
}

.dark-mode .viewer-card {
  background: var(--app-form-tile);
}

.dark-mode .viewer-card__tabs {
  background: var(--app-form-tile);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .viewer-tab {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text-muted);
}

.dark-mode .viewer-tab:hover {
  background: var(--app-accent-soft);
}

.dark-mode .viewer-card__body {
  background: var(--app-elevated);
}

.dark-mode .viewer-card__empty-text {
  color: var(--app-text-faint);
}

.dark-mode .panel-textarea {
  border: 1px solid var(--app-field-border);
  color: var(--app-text);
  background: var(--app-field);
}

.dark-mode .panel-textarea::placeholder {
  color: var(--app-field-placeholder);
}

.dark-mode .section-card--grow .section-body::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
}

.dark-mode .checklist-progress-strip {
  background: var(--app-surface);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .checklist-progress {
  background: var(--app-form-tile);
}

.dark-mode .checklist-progress-label {
  color: var(--app-text-muted);
}

.dark-mode .checklist-error {
  background: var(--app-danger-bg);
}

.dark-mode .checklist-item {
  border: 1px solid var(--app-border);
}

.dark-mode .checklist-item:hover {
  border-color: var(--app-border-strong);
  background: var(--app-form-tile-hover) !important;
}

.dark-mode .checklist-item--checked {
  background: var(--app-success-bg) !important;
}

.dark-mode .checklist-item--checked:hover {
  background: var(--app-success-bg) !important;
}

.dark-mode .custom-check {
  border: 2px solid var(--app-border-strong);
  background: var(--app-form-tile);
}

.dark-mode .checklist-item__title {
  color: var(--app-text) !important;
}

.dark-mode .checklist-item__subtitle {
  color: var(--app-text-muted) !important;
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE
   ============================================ */
.rv-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER
   ============================================ */
.rv-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.rv-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.rv-header__info {
  flex: 1;
  min-width: 0;
}

.rv-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.rv-header__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 16px;
}

/* Hero action buttons */
.hero-action-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.hero-action-btn:hover {
  transform: translateY(-1px);
}

.hero-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.hero-action-btn--red {
  background: #fef2f2;
  color: #ef4444;
}

.hero-action-btn--red:hover {
  background: #fee2e2;
  color: #dc2626;
}

.hero-action-btn--green {
  background: #dcfce7;
  color: #16a34a;
}

.hero-action-btn--green:hover {
  background: #d1fae5;
  color: #059669;
}

/* ============================================
   SPLIT PANE LAYOUT
   ============================================ */
.split-pane {
  display: flex;
  gap: 20px;
  align-items: stretch;
  height: calc(100vh - 180px);
  min-height: 500px;
}

.split-pane__viewer {
  flex: 1;
  min-width: 0;
}

.split-pane__panel {
  flex: 0 0 380px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header--blue {
  background: linear-gradient(135deg, #3b82f6, #60a5fa);
}

.section-header--green {
  background: linear-gradient(135deg, #10b981, #34d399);
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-body {
  padding: 22px;
  background: #fff;
}

/* ============================================
   DETAIL ROW (read-only display like View.vue)
   ============================================ */
.detail-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  margin-bottom: 8px;
}

.detail-label {
  flex: 0 0 140px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 1px;
}

.detail-val {
  flex: 1;
  font-size: 0.85rem;
  color: #1a1e24;
  line-height: 1.5;
  word-break: break-word;
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  background: #f1f3f5;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 7px 14px;
}

/* ============================================
   VIEWER CARD (document preview)
   ============================================ */
.viewer-card {
  background: #eaecef;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.viewer-card__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.viewer-tab {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.viewer-tab:hover {
  background: #f0f9ff;
  border-color: #bae6fd;
  color: #0369a1;
}

.viewer-tab--active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: #fff;
}

.viewer-tab--active:hover {
  background: #0284c7;
  border-color: #0284c7;
  color: #fff;
}

.viewer-card__body {
  flex: 1;
  min-height: 0;
  background: #f1f5f9;
}

.viewer-card__iframe {
  border: none;
  display: block;
}

.viewer-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
}

.viewer-card__empty-text {
  font-size: 0.88rem;
  color: #94a3b8;
  font-weight: 500;
}

/* ============================================
   PANEL FORM
   ============================================ */
.panel-form-group {
  margin-top: 14px;
}

.panel-form-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: #2d3239;
  margin-bottom: 6px;
}

.panel-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  color: #1e293b;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  background: #fff;
  box-sizing: border-box;
}

.panel-textarea::placeholder {
  color: #9ca3af;
}

.panel-textarea:focus {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.12);
}

.section-card--grow {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.section-card--grow .section-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.section-body--compact {
  padding: 16px 18px;
}

/* Scrollbar styling */
.section-card--grow .section-body::-webkit-scrollbar {
  width: 5px;
}

.section-card--grow .section-body::-webkit-scrollbar-track {
  background: transparent;
}

.section-card--grow .section-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.section-card--grow .section-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* ============================================
   CHECKLIST
   ============================================ */
.checklist-counter {
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 10px;
  border-radius: 20px;
}

.checklist-progress-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 2;
  flex-shrink: 0;
}

.checklist-progress {
  flex: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.checklist-progress__bar {
  height: 100%;
  background: linear-gradient(90deg, #f59e0b, #10b981);
  border-radius: 6px;
  transition: width 0.3s ease;
}

.checklist-progress__bar--complete {
  background: linear-gradient(90deg, #10b981, #059669);
}

.checklist-progress-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  white-space: nowrap;
  flex-shrink: 0;
}

.checklist-error {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  margin-bottom: 14px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #dc2626;
}

.checklist-list {
  padding: 0 !important;
  background: transparent !important;
}

.checklist-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px !important;
  padding: 10px 12px !important;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  min-height: auto !important;
}

.checklist-item::before {
  opacity: 0 !important;
}

.checklist-item:last-child {
  margin-bottom: 0;
}

.checklist-item:hover {
  border-color: #cbd5e1;
  background: #f8fafc !important;
}

.checklist-item--checked {
  background: #f0fdf4 !important;
  border-color: #86efac !important;
}

.checklist-item--checked:hover {
  background: #ecfdf5 !important;
}

.checklist-item__action {
  align-self: center;
  margin-right: 10px !important;
  min-width: auto !important;
}

/* Custom checkbox */
.custom-check {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  cursor: pointer;
  background: #fff;
}

.custom-check--active {
  background: #10b981;
  border-color: #10b981;
}

.checklist-item__content {
  padding: 0;
}

.checklist-item__title {
  font-size: 0.82rem !important;
  font-weight: 700 !important;
  color: #1e293b !important;
  white-space: normal !important;
}

.checklist-item__subtitle {
  font-size: 0.78rem !important;
  color: #64748b !important;
  margin-top: 2px;
  line-height: 1.5 !important;
  white-space: normal !important;
}

.checklist-item--checked .checklist-item__title {
  color: #065f46 !important;
}

.checklist-item--checked .checklist-item__subtitle {
  color: #047857 !important;
}

.checklist-item__status {
  flex-shrink: 0;
  margin-left: 8px;
}

/* ============================================
   FADE TRANSITION
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 1264px) {
  .split-pane__panel {
    flex: 0 0 340px;
  }
}

@media (max-width: 959px) {
  .split-pane {
    flex-direction: column;
  }

  .split-pane__viewer {
    position: static;
  }

  .split-pane__panel {
    flex: none;
    width: 100%;
  }

  .rv-header__top {
    padding: 16px 20px;
  }

  .rv-header__title {
    font-size: 1rem;
  }

  .section-body {
    padding: 16px;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-label {
    flex: none;
  }

  .detail-val {
    width: 100%;
  }

  .split-pane {
    height: auto;
  }

  .viewer-card__body {
    height: 50vh;
    min-height: 300px;
  }

  .section-card--grow {
    max-height: none;
  }

  .viewer-card__tabs {
    gap: 3px;
    padding: 8px;
  }

  .viewer-tab {
    font-size: 0.68rem;
    padding: 5px 8px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .rv-page { background-color: var(--app-page); }
.dark-mode .rv-header { background: var(--app-card); }
.dark-mode .rv-header__title { color: var(--app-text); }
.dark-mode .back-btn { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .back-btn:hover { background: var(--app-form-tile-hover); }
.dark-mode .section-card { background: var(--app-card); }
.dark-mode .section-body { background: var(--app-card); }
.dark-mode .detail-label { color: var(--app-text-muted); }
.dark-mode .detail-val { background: var(--app-form-tile); border-color: var(--app-form-tile-border); color: var(--app-text); }
.dark-mode .split-pane__viewer .viewer-card { background: var(--app-form-tile); }
.dark-mode .viewer-card__tabs { background: var(--app-form-tile); border-color: var(--app-border); }
.dark-mode .viewer-tab { background: var(--app-form-tile); border-color: var(--app-form-tile-border); color: var(--app-text-muted); }
.dark-mode .viewer-tab:hover { background: var(--app-form-tile-hover); border-color: var(--app-border-strong); color: var(--app-text); }
.dark-mode .viewer-card__body { background: var(--app-card); }
.dark-mode .panel-textarea { background: var(--app-field); border-color: var(--app-field-border); color: var(--app-text); }
.dark-mode .panel-form-label { color: var(--app-text-muted); }
.dark-mode .checklist-item { border-color: var(--app-border); }
.dark-mode .checklist-item:hover { background: var(--app-border) !important; }
.dark-mode .checklist-item--checked { background: var(--app-success-bg) !important; border-color: var(--app-success-fg) !important; }
.dark-mode .checklist-item__title { color: var(--app-text) !important; }
.dark-mode .checklist-item__subtitle { color: var(--app-text-muted) !important; }
.dark-mode .custom-check { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .checklist-progress-strip { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .checklist-error { background: var(--app-danger); border-color: var(--app-danger-fg); color: var(--app-danger-fg); }

</style>
